/**
 * Trades Poster — Team Members workflow helpers (validation, actions, display).
 */

import {
  APPROVED_WORK_COUNTRIES,
  generateTemporaryPassword,
  isOfficialWorkEmail,
  memberHasValidStoredAccess,
  mobileMatchesApprovedCountry,
  NOT_APPLICABLE,
  NOT_ASSIGNED,
  OFFICIAL_EMAIL_DOMAIN,
  SYSTEM_PROTECTED_ACCESS_LABEL,
  TEAM_MEMBER_STATUS,
  buildPermissionsAccessLabel,
} from './teamConfig';
import {
  getModuleLabel,
  moduleHasAnyPermission,
  normalizeModulePermissions,
} from './permissionsConfig';
import { recordChangeHistory } from './changeHistoryStore';
import { CHANGE_HISTORY_DISPLAY } from './changeHistoryEntry';

function emitTeamMemberHistory(member, { changeType, activityDetails, actorName, stamp }) {
  if (!member?.id) return;
  try {
    recordChangeHistory({
      sourceChangeRef: `team_members::${member.id}::${changeType}::${stamp}`,
      sourceModuleRef: 'team_members',
      sourceItemRef: member.id,
      module: 'Team Members',
      changedItem: member.name || member.id,
      itemId: member.id,
      changeType,
      activityOnly: true,
      activityDetails,
      changeReason: CHANGE_HISTORY_DISPLAY.NOT_REQUIRED,
      changedBy: { name: actorName, role: 'Platform Owner' },
      changedOnIso: stamp,
      accountRef: member.id,
    });
  } catch {
    /* history recording failure must not reverse the source save */
  }
}

export {
  SYSTEM_PROTECTED_ACCESS_LABEL as OWNER_ACCESS_LABEL,
  SYSTEM_PROTECTED_ACCESS_LABEL as CO_OWNER_ACCESS_LABEL,
  SYSTEM_PROTECTED_ACCESS_LABEL as OWNER_PERMISSION_LABEL,
  SYSTEM_PROTECTED_ACCESS_LABEL as CO_OWNER_PERMISSION_LABEL,
  SYSTEM_PROTECTED_ACCESS_LABEL,
};

export const RESET_PASSWORD_NOTICE =
  'The current password will stop working and active sessions will be revoked.';
export const ACTIVATE_NOTICE =
  'Valid stored access will resume, subject to current verification and security requirements.';
export const DEACTIVATE_NOTICE = 'Sign-in and live module access will stop.';

export const ACTIVATE_BLOCKED_NO_ACCESS =
  'Activate is blocked. No valid stored access assignment remains.';

export const LIST_ACTION_LABELS = {
  view: 'View',
  edit: 'Edit',
  resetPassword: 'Reset Password',
  deactivate: 'Deactivate',
  activate: 'Activate',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidTeamEmail(value) {
  return EMAIL_PATTERN.test((value || '').trim());
}

export function getMemberPermissionsAccessDisplay(member) {
  if (!member) return '—';
  if (member.isOwner || member.isCoOwner) return SYSTEM_PROTECTED_ACCESS_LABEL;
  if (member.permissionsAccessLabel) return member.permissionsAccessLabel;
  return buildPermissionsAccessLabel(member);
}

/** Module labels granted on the member’s permission matrix (for list preview / View All). */
export function getMemberPermissionModuleNames(member) {
  if (!member || member.isOwner || member.isCoOwner) return [];
  return normalizeModulePermissions(member.modulePermissions || [])
    .filter(moduleHasAnyPermission)
    .map((row) => getModuleLabel(row.module))
    .filter(Boolean);
}

/** @deprecated Prefer getMemberPermissionsAccessDisplay */
export function getMemberPermissionDisplay(member) {
  return getMemberPermissionsAccessDisplay(member);
}

export function getMemberTeamDisplay(member) {
  if (!member) return '—';
  if (member.isOwner || member.isCoOwner) return NOT_APPLICABLE;
  if (!member.team || member.team === NOT_ASSIGNED) return NOT_ASSIGNED;
  return member.team;
}

export function getMemberJobRoleDisplay(member) {
  if (!member) return '—';
  if (member.isOwner || member.isCoOwner) return NOT_APPLICABLE;
  if (!member.position || member.position === NOT_ASSIGNED) return NOT_ASSIGNED;
  return member.position;
}

/** @deprecated Prefer getMemberJobRoleDisplay */
export function getMemberPositionDisplay(member) {
  return getMemberJobRoleDisplay(member);
}

export function getMemberPlatformRoleDisplay(member) {
  if (!member) return '—';
  if (member.isOwner) return 'Platform Owner';
  if (member.isCoOwner) return 'Co-owner';
  return member.platformRole || 'Team Member';
}

export function isProtectedMember(member) {
  return Boolean(member?.isOwner || member?.isCoOwner);
}

export function getMemberStatusKey(member) {
  const raw = String(member?.status || '').trim();
  if (raw === 'Deactivated') return TEAM_MEMBER_STATUS.INACTIVE;
  if (raw === 'Password Reset Required') return TEAM_MEMBER_STATUS.ACTIVE;
  return raw;
}

export function getTeamListActions(member, { canManage = false } = {}) {
  if (!member) return [];
  const actions = [LIST_ACTION_LABELS.view];
  if (!canManage || isProtectedMember(member)) return actions;

  const status = getMemberStatusKey(member);
  actions.push(LIST_ACTION_LABELS.edit);

  if (status === TEAM_MEMBER_STATUS.PENDING_ACCESS) {
    return actions;
  }

  if (status === TEAM_MEMBER_STATUS.ACTIVE) {
    actions.push(LIST_ACTION_LABELS.resetPassword, LIST_ACTION_LABELS.deactivate);
    return actions;
  }

  if (status === TEAM_MEMBER_STATUS.INACTIVE) {
    actions.push(LIST_ACTION_LABELS.resetPassword, LIST_ACTION_LABELS.activate);
  }

  return actions;
}

export function normalizeMobileKey(value) {
  return String(value || '').replace(/[\s\-()]/g, '');
}

export function collectMemberSaveValidationErrors(
  form,
  { members = [], excludeMemberId = null } = {},
) {
  const errors = {};
  const firstName = (form.firstName || '').trim();
  const lastName = (form.lastName || '').trim();
  const workEmail = (form.workEmail || '').trim();
  const mobileNumber = (form.mobileNumber || '').trim();
  const approvedWorkCountry = (form.approvedWorkCountry || '').trim();
  const emailKey = workEmail.toLowerCase();
  const mobileKey = normalizeMobileKey(mobileNumber);

  if (!firstName) errors.firstName = 'Enter First Name.';
  if (!lastName) errors.lastName = 'Enter Last Name.';

  if (!workEmail) {
    errors.workEmail = 'Enter Work Email.';
  } else if (!isValidTeamEmail(workEmail)) {
    errors.workEmail = 'Enter a valid Work Email.';
  } else if (!isOfficialWorkEmail(workEmail)) {
    errors.workEmail = `Work Email must use @${OFFICIAL_EMAIL_DOMAIN}.`;
  } else {
    const duplicate = (members || []).some(
      (m) =>
        m.id !== excludeMemberId &&
        (m.workEmail || '').trim().toLowerCase() === emailKey,
    );
    if (duplicate) errors.workEmail = 'This Work Email is already in use.';
  }

  if (!approvedWorkCountry) {
    errors.approvedWorkCountry = 'Select Approved Work Country.';
  } else if (!APPROVED_WORK_COUNTRIES.some((c) => c.value === approvedWorkCountry)) {
    errors.approvedWorkCountry = 'Select Australia or Pakistan.';
  }

  if (!mobileNumber) {
    errors.mobileNumber = 'Enter Mobile Number with Country Code.';
  } else if (
    approvedWorkCountry &&
    !mobileMatchesApprovedCountry(mobileNumber, approvedWorkCountry)
  ) {
    errors.mobileNumber = 'Mobile Number must match the Approved Work Country code.';
  } else if (mobileKey) {
    const duplicateMobile = (members || []).some(
      (m) =>
        m.id !== excludeMemberId &&
        normalizeMobileKey(m.mobileNumber) === mobileKey,
    );
    if (duplicateMobile) errors.mobileNumber = 'This Mobile Number is already in use.';
  }

  return errors;
}

export function createPendingAccessMember(form, { actorName = 'Platform Owner' } = {}) {
  const now = new Date().toISOString();
  const firstName = form.firstName.trim();
  const lastName = form.lastName.trim();
  const name = `${firstName} ${lastName}`.trim();

  const member = {
    id: `tm-${Date.now()}`,
    teamMemberId: null,
    name: name || 'Team Member',
    firstName,
    lastName,
    workEmail: form.workEmail.trim(),
    mobileNumber: form.mobileNumber.trim(),
    approvedWorkCountry: form.approvedWorkCountry.trim(),
    team: NOT_ASSIGNED,
    position: NOT_ASSIGNED,
    jobTitle: NOT_ASSIGNED,
    platformRole: 'Team Member',
    permissionId: null,
    permissionName: NOT_ASSIGNED,
    permissionPreset: null,
    customPermission: null,
    hasCustomisedAccess: false,
    permissionsAdded: [],
    permissionsRemoved: [],
    modulePermissions: [],
    defaultAccessLabel: null,
    permissionsAccessLabel: NOT_ASSIGNED,
    dashboard: null,
    status: TEAM_MEMBER_STATUS.PENDING_ACCESS,
    memberSince: now,
    lastUpdated: now,
    lastUpdatedBy: actorName,
    isOwner: false,
    isCoOwner: false,
    requirePasswordChange: false,
  };
  emitTeamMemberHistory(member, {
    changeType: 'Add',
    activityDetails: 'Team Member created with Pending Access',
    actorName,
    stamp: now,
  });
  return member;
}

/**
 * Reset password for Active or Inactive ordinary members.
 * Does not change Status. Shows one-time temporary password to the Owner.
 */
export function applyPasswordReset(member, { actorName = 'Platform Owner' } = {}) {
  const temporaryPassword = generateTemporaryPassword();
  const now = new Date().toISOString();
  const next = {
    ...member,
    requirePasswordChange: true,
    lastUpdated: now,
    lastUpdatedBy: actorName,
  };
  emitTeamMemberHistory(next, {
    changeType: 'Password Reset Requested',
    activityDetails: 'Protected credential: Updated. Password-reset link/result recorded.',
    actorName,
    stamp: now,
  });
  return {
    member: next,
    temporaryPassword,
  };
}

export function applyDeactivate(member, { actorName = 'Platform Owner' } = {}) {
  const now = new Date().toISOString();
  const next = {
    ...member,
    status: TEAM_MEMBER_STATUS.INACTIVE,
    lastUpdated: now,
    lastUpdatedBy: actorName,
  };
  emitTeamMemberHistory(next, {
    changeType: 'Account Status Changed',
    activityDetails: 'Status changed to Inactive',
    actorName,
    stamp: now,
  });
  return next;
}

export function applyActivate(member, { actorName = 'Platform Owner' } = {}) {
  if (!memberHasValidStoredAccess(member)) {
    return { member, blocked: true, reason: ACTIVATE_BLOCKED_NO_ACCESS };
  }
  const now = new Date().toISOString();
  const next = {
    ...member,
    status: TEAM_MEMBER_STATUS.ACTIVE,
    lastUpdated: now,
    lastUpdatedBy: actorName,
  };
  emitTeamMemberHistory(next, {
    changeType: 'Account Status Changed',
    activityDetails: 'Status changed to Active',
    actorName,
    stamp: now,
  });
  return {
    member: next,
    blocked: false,
  };
}

/** @deprecated Prefer applyActivate */
export function applyReactivate(member, options) {
  const result = applyActivate(member, options);
  return result.member;
}

export function isResetPasswordEligible(member) {
  if (!member || isProtectedMember(member)) return false;
  const status = getMemberStatusKey(member);
  return status === TEAM_MEMBER_STATUS.ACTIVE || status === TEAM_MEMBER_STATUS.INACTIVE;
}

export function isActivateEligible(member) {
  if (!member || isProtectedMember(member)) return false;
  return getMemberStatusKey(member) === TEAM_MEMBER_STATUS.INACTIVE;
}

export function isDeactivateEligible(member) {
  if (!member || isProtectedMember(member)) return false;
  return getMemberStatusKey(member) === TEAM_MEMBER_STATUS.ACTIVE;
}
